<template>
  <div class="transition-picker">
    <div class="transition-picker__container">
      <div class="dropdown" :class="{ 'is-active': isOpen, 'is-up': isUp }">
        <div class="dropdown-trigger" @click="togglePicker" v-click-away="closePicker">
          <button class="button" :class="{ fixed: fixed, 'button--loading': !loaded }">
            <PathIcon
              v-if="loaded && isTypeSelected(2)"
              class="icon-1-3x selected-icon fa-flip-vertical path-icon"
            ></PathIcon>
            <FlowIcon v-if="loaded && isTypeSelected(3)" class="icon-1-3x selected-icon" />
            <AnswerIcon v-if="loaded && isTypeSelected(4)" class="answers-icon selected-answer" />

            <span class="selected-transition">{{ getSelectedOptionName() }}</span>
            <span class="icon" v-if="loaded && !fixed">
              <font-awesome-icon icon="angle-down"></font-awesome-icon>
            </span>
          </button>
        </div>
        <div class="dropdown-menu" id="dropdown-menu" role="menu" v-if="loaded">
          <div class="dropdown-content">
            <div v-if="noneTransition && showNoneTransition">
              <a class="dropdown-item" @click="chooseOption(noneTransition)">
                <div class="add-item">None</div>
              </a>
              <hr class="dropdown-divider" />
            </div>
            <div v-if="isPathOptionsVisible">
              <div class="dropdown-item item-group-heading">
                <div class="heading-container">
                  <PathIcon class="icon-1-5x dropdown-icons fa-flip-vertical path-icon"></PathIcon>
                  <span class="heading-with-icon">Paths</span>
                </div>
              </div>
              <div>
                <a
                  v-for="option in pathOptions"
                  :key="option.id"
                  @click="chooseOption(option)"
                  class="dropdown-item dropdown-child"
                >
                  {{ option.name }}
                </a>
                <a class="dropdown-item" @click="addNewPath">
                  <div class="add-item">
                    <span class="add-path">
                      <AddIcon class="icon-1-3x"></AddIcon>
                      Add new path
                    </span>
                  </div>
                </a>
              </div>
            </div>
            <div v-if="isFlowOptionsVisible">
              <hr class="dropdown-divider" v-if="isPathOptionsVisible" />
              <div class="dropdown-item item-group-heading">
                <div class="heading-container">
                  <FlowIcon class="icon-1-5x dropdown-icons"></FlowIcon>
                  <span class="heading-with-icon">Flows</span>
                </div>
              </div>
              <div>
                <a
                  v-for="option in flowOptions"
                  :key="option.id"
                  @click="chooseOption(option)"
                  class="dropdown-item dropdown-child"
                >
                  {{ option.name }}
                </a>
              </div>
              <a v-if="canAddNewFlow" class="dropdown-item" @click="showModal = true">
                <div class="add-item">
                  <span class="add-flow">
                    <AddIcon class="icon-1-3x"></AddIcon>
                    Add new flow
                  </span>
                </div>
              </a>
            </div>
            <div v-if="isAnswerOptionsVisible">
              <hr class="dropdown-divider" v-if="isPathOptionsVisible || isFlowOptionsVisible" />
              <div class="dropdown-item item-group-heading">
                <div class="heading-container">
                  <AnswerIcon v-if="loaded && isTypeSelected(4)" class="answers-icon" />
                  <span class="answers-heading">FAQs</span>
                </div>
              </div>
              <div>
                <a
                  v-for="option in answerOptions"
                  :key="option.id"
                  @click="chooseOption(option)"
                  class="dropdown-item dropdown-child"
                >
                  {{ option.name }}
                </a>
              </div>
            </div>
          </div>
        </div>
        <div class="goto-button" v-if="!isNoneChosen()">
          <button class="button" @click="goToTransition()" :disabled="isGoToButtonDisabled">
            <span class="icon">
              <font-awesome-icon icon="arrow-right"></font-awesome-icon>
            </span>
          </button>
        </div>
      </div>
    </div>
    <AddFlowModal v-if="showModal" @close="showModal = false" @flow-added="chooseFlow"></AddFlowModal>
  </div>
</template>

<script lang="ts" setup>
import { PathViewModel } from '@/features/bots/models/designer';
import { dropDownHelper } from '@/shared/components/dropDownMenu/dropDownHelper';
import { ADD_PATH, SET_CURRENT_PATH } from '@/store/bot-designer/paths/mutationTypes';
import { TransitionViewModel } from '../../models/designer/transitionViewModel';
import { TransitionType } from '@/features/bots/models/designer/transitionType';
import PathIcon from 'vue-material-design-icons/CallSplit.vue';
import FlowIcon from 'vue-material-design-icons/Waves.vue';
import { nanoid } from 'nanoid';
import { FlowListItemViewModel } from '@/features/bots/models/flowListItemViewModel';
import AddIcon from 'vue-material-design-icons/Plus.vue';
import Toast from '@/shared/services/toast';
import AddFlowModal from '@/shared/components/AddFlowModal.vue';
import { FlowStatus } from '@/features/bots/models/flowStatus';
import { TransitionPickerOption } from '../../models/designer/transitionPickerOption';
import { TransitionPickerConfig } from '../../models/designer/transitionPickerConfig';
import { LOAD_FLOWS } from '@/store/bot-designer/actionTypes';
import { LOAD_ANSWERS } from '@/store/answers/actionTypes';
import { AnswerListItemViewModel } from '../../models/answers/answerListItemViewModel';
import { computed, nextTick, onMounted, PropType, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import AnswerIcon from '@shared/icons/AnswerIcon.vue';

const props = defineProps({
  transition: {
    type: Object as PropType<TransitionViewModel>,
  },
  fixed: {
    type: Boolean,
    default: false,
  },
  showNoneTransition: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits<{
  (e: 'transition-selected', value: TransitionViewModel): void;
}>();

const router = useRouter();

const store = useStore();
const transitionPickerConfigForAnswer = computed<TransitionPickerConfig>(
  () => store.getters.transitionPickerConfigForAnswer,
);
const transitionPickerConfigForFlow = computed<TransitionPickerConfig>(
  () => store.getters.transitionPickerConfigForFlow,
);
const transitionPickerFixedConfig = computed<TransitionPickerConfig>(() => store.getters.transitionPickerFixedConfig);
const isTransitionPayloadInvalid = (payload: string) =>
  computed(() => store.getters.isTransitionPayloadInvalid(payload));

const loadFlows = (forceLoad = false): Promise<FlowListItemViewModel[]> => store.dispatch(LOAD_FLOWS, forceLoad);
const loadAnswers = (doLoad = false): Promise<AnswerListItemViewModel[]> => store.dispatch(LOAD_ANSWERS, doLoad);

const setCurrentPath = (id: string) => store.commit(SET_CURRENT_PATH, id);
const addPath = (path: PathViewModel) => store.commit(ADD_PATH, path);

const selectedLabel = ref('');
const isOpen = ref(false);
const isUp = ref(false);
const selectedTransition = ref<TransitionViewModel>({} as TransitionViewModel);
const showModal = ref(false);
const loaded = ref(false);

const pathOptions = computed(() =>
  options.value.filter((x) => x.type === TransitionType.Path).sort((a, b) => a.name.localeCompare(b.name)),
);
const flowOptions = computed(() =>
  options.value.filter((x) => x.type === TransitionType.Flow).sort((a, b) => a.name.localeCompare(b.name)),
);
const answerOptions = computed(() =>
  options.value.filter((x) => x.type === TransitionType.Answer).sort((a, b) => a.name.localeCompare(b.name)),
);
const noneTransition = computed(() => options.value.find((x) => x.type === TransitionType.None));
const options = computed(() => config.value?.options || []);
const canAddNewFlow = computed(() => config.value.currentStatus !== FlowStatus.Live);
const isPathOptionsVisible = computed(
  () => (pathOptions.value && pathOptions.value.length > 0) || config.value.allowAddPath,
);
const isFlowOptionsVisible = computed(() => (flowOptions.value && flowOptions.value.length > 0) || canAddNewFlow.value);
const isAnswerOptionsVisible = computed(() => answerOptions.value && answerOptions.value.length > 0);
const isGoToButtonDisabled = computed(() => !loaded.value || !isTransitionValid.value);
const isTransitionValid = computed(
  () => props.transition?.payload && !isTransitionPayloadInvalid(props.transition.payload).value,
);
const config = computed<TransitionPickerConfig>(() => {
  if (props.fixed) {
    return transitionPickerFixedConfig.value;
  }
  if (isOnAnswersPage()) {
    return transitionPickerConfigForAnswer.value;
  }
  return transitionPickerConfigForFlow.value;
});

watch(
  () => props.transition,
  (value: TransitionViewModel) => (selectedTransition.value = value || ({} as TransitionViewModel)),
  { immediate: true },
);
onMounted(() => loadFlowsAndAnswers());

function getSelectedOptionName(): string {
  if (!loaded.value) {
    return '';
  }
  if (selectedTransition.value?.payload) {
    if (isTransitionPayloadInvalid(selectedTransition.value.payload).value) {
      return 'Invalid transition';
    }
  }
  if (!selectedLabel.value) {
    selectedLabel.value = options.value.find((x) => x.id === selectedTransition.value?.payload)?.name || '';
  }
  if (!selectedLabel.value) {
    return 'Choose a transition';
  }

  return selectedLabel.value;
}

function closePicker() {
  isOpen.value = false;
  isUp.value = false;
}

function togglePicker() {
  // don't allow opening the menu/choosing a path if the picker should be 'fixed'
  if (props.fixed) {
    return;
  }

  isOpen.value = !isOpen.value;

  nextTick(() => (isUp.value = dropDownHelper.isDropDownUp()));
}

function goToTransition() {
  if (typeof selectedTransition.value.payload === 'string') {
    if (selectedTransition.value.type === TransitionType.Path) {
      setCurrentPath(selectedTransition.value.payload);
    } else if (selectedTransition.value.type === TransitionType.Flow) {
      goToFlow();
    } else if (selectedTransition.value.type === TransitionType.Answer) {
      goToAnswer();
    }
  }
}

function chooseOption(option: TransitionPickerOption) {
  selectedLabel.value = option.name;
  chooseTransition(option.id, option.type);
  closePicker();
}

function chooseFlow(flow: FlowListItemViewModel) {
  selectedLabel.value = flow.name;
  chooseTransition(flow.id, TransitionType.Flow);
}

function chooseTransition(id: string, transitionType: TransitionType) {
  selectedTransition.value = {
    payload: id,
    type: transitionType,
  } as TransitionViewModel;

  emit('transition-selected', selectedTransition.value);
}

function isTypeSelected(type: TransitionType) {
  return selectedTransition.value?.type === type;
}

async function goToFlow() {
  try {
    if (selectedTransition.value && selectedTransition.value.payload) {
      router.push({
        name: 'bots-flow-designer',
        params: { flowId: selectedTransition.value.payload },
      });
    } else {
      Toast.showError("Can't find Flow");
    }
  } catch (err: any) {
    Toast.showError(err.message);
  }
}

async function goToAnswer() {
  try {
    if (selectedTransition.value && selectedTransition.value.payload) {
      router.push({
        name: 'bots-answer-editor',
        params: { id: selectedTransition.value.payload },
      });
    }
  } catch (err: any) {
    Toast.showError(err.message);
  }
}

function addNewPath() {
  const newPath = {
    id: `path-${nanoid(7)}`,
    name: `New Path`,
    states: [],
  } as PathViewModel;

  addPath(newPath);

  selectedLabel.value = newPath.name;

  selectedTransition.value = {
    type: TransitionType.Path,
    payload: newPath.id,
  };
  emit('transition-selected', selectedTransition.value);
}

function isNoneChosen() {
  return props.transition && props.transition.type === TransitionType.None;
}

function isOnAnswersPage() {
  return getRouteName() === 'bots-answer-editor';
}

function getRouteName() {
  return router.currentRoute.value.name;
}

async function loadFlowsAndAnswers(): Promise<void> {
  loaded.value = false;
  try {
    await Promise.all([loadFlows(), loadAnswers()]);
  } catch (e: any) {
    Toast.showError(e.message);
  }
  loaded.value = true;
}
</script>

<style scoped lang="scss">
.transition-picker {
  display: flex;
  flex: 1;
  &__container {
    display: flex;
    flex: 1;
    border-radius: 5px;
  }
}

.dropdown {
  display: flex;
  flex: 1;
  background-color: white;
  width: 100%;

  .dropdown-trigger {
    width: 100%;

    .selected-transition {
      position: relative;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      width: 100%;
      text-align: left;

      color: $color-netcall-deep-sea;
    }
  }
}

.dropdown-content {
  max-height: 300px;
  overflow: auto;
}

.dropdown-divider {
  background-color: #ededed;
  border: none;
  display: block;
  height: 1px;
  margin: 0.5rem 0;
}

.button {
  background-color: transparent;
  border-radius: 4px;
  color: inherit;
  border: solid 1px #d9d9d9;
  justify-content: left;

  width: 100%;

  &:hover {
    color: $color-netcall-body-text;
    border-color: $color-netcall-body-text;
  }

  &:focus {
    color: $color-netcall-body-text;
    border-color: $color-netcall-body-text;
  }

  &.fixed {
    cursor: default;

    &:focus:not(:active) {
      box-shadow: none;
    }
  }

  &:focus {
    box-shadow: none;
  }
}

.arrow-right {
  margin-top: 5px;
  margin-left: 5px;
  color: $color-netcall-jonquil;
  cursor: pointer;
}

.item-group-heading {
  font-weight: bold;
  font-size: small;
}

.dropdown-child {
  text-indent: 6px;
}

.heading-container {
  height: 20px;

  .heading-with-icon {
    top: 30%;
    position: absolute;
    margin-left: 5px;
  }

  .answers-heading {
    top: 30%;
    position: absolute;
  }

  .answers-icon {
    display: inline-block;
    position: relative;
  }

  .answer-icon {
    margin: auto;
  }
}

.add-item {
  display: flex;

  .icon-1-3x {
    position: relative;
    top: 2px;
    color: $color-netcall-deep-sea;
  }

  .add-path,
  .add-flow {
    color: $color-netcall-deep-sea;
  }
}

.selected-icon {
  position: relative;
  bottom: 2px;
  margin-right: 10px;
  color: $color-netcall-deep-sea;
}

.selected-answer {
  position: relative;
  margin-top: 5px;
  margin-right: 8px;
  fill: $color-netcall-deep-sea;
}

/*Old styling*/
.path-picker {
  display: flex;
  flex: 1;
  border-radius: 5px;
}

.dropdown {
  display: flex;
  flex: 1;
  background-color: $color-netcall-jonquil;
  width: 150px;

  .dropdown-trigger {
    overflow: hidden;

    .selected-path {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}

.goto-button {
  display: flex;
  align-items: center;
  background-color: $color-netcall-deep-sea;
  color: $color-netcall-jonquil;

  &:hover {
    cursor: pointer;
    background-color: $color-netcall-jonquil;
    color: $color-netcall-deep-sea;
  }

  .button[disabled] {
    color: white;
    background-color: #d4d9e3;
    opacity: 0.9;
  }
}

.dropdown-divider {
  background-color: #ededed;
  border: none;
  display: block;
  height: 1px;
  margin: 0.5rem 0;
}

.button {
  background-color: transparent;
  border: none;
  border-radius: 0px;
  color: inherit;
  width: 100%;

  &--loading::after {
    border: 2px solid #dbdbdb;
    border-radius: 290486px;
    border-right-color: transparent;
    border-top-color: transparent;
    content: '';
    display: block;
    height: 1em;
    width: 1em;
    animation: spinAround 500ms infinite linear;
    left: calc(50% - 0.5em);
    top: calc(50% - 0.5em);
    position: absolute !important;
  }

  &.fixed {
    cursor: default;

    &:focus:not(:active) {
      box-shadow: none;
    }
  }

  &:focus {
    box-shadow: none;
  }
}

.path-icon {
  top: 3px;
}
</style>
